import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

import { getCookie } from "../utils/cookie/getCookie";

export const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

export const request = (params: AxiosRequestConfig): Promise<AxiosResponse> => {
  return axiosInstance(params);
};


/**
 * Creates a Server-Sent Events (SSE) connection using Axios with streaming support.
 * 
 * @param url - The URL endpoint to establish the SSE connection with
 * @param config - Optional Axios request configuration to customize the request
 * @param onMessage - Optional callback function invoked when a message is received from the server
 * @param onError - Optional callback function invoked when an error occurs during the connection
 * @param onOpen - Optional callback function invoked when the SSE connection is successfully opened
 * 
 * @returns An object containing:
 *   - `start`: Async function to initiate the SSE connection
 *   - `close`: Function to abort the SSE connection using AbortController
 * 
 * @example
 * ```typescript
 * const sseConnection = createSSEConnection(
 *   'https://api.example.com/events',
 *   { timeout: 30000 },
 *   (data) => console.log('Received:', data),
 *   (error) => console.error('SSE Error:', error),
 *   () => console.log('SSE Connected')
 * );
 * 
 * await sseConnection.start();
 * // Later, to close the connection:
 * sseConnection.close();
 * ```
 */
export const createSSEConnection = (
  url: string,
  config?: AxiosRequestConfig,
  onMessage?: (data: any) => void,
  onError?: (error: any) => void,
  onOpen?: () => void
) => {
  const controller = new AbortController();

  const startSSE = async () => {
    try {
      const response = await fetch(url, {
        method: "GET",
        // @ts-ignore
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          ...config?.headers
        },

        // @ts-ignore
        signal: controller.signal,
        ...config
      });

      onOpen?.();

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (reader) {
        const { done, value } = await reader.read();
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              onMessage?.(data);
            } catch (e) {
              console.warn('Failed to parse SSE data:', line);
              onMessage?.(line.slice(6)); // Send raw data if JSON parsing fails
            }
          }
        }
      }

    } catch (error) {
      onError?.(error);
    }
  };

  return {
    start: startSSE,
    close: () => controller.abort()
  };
};

axiosInstance.interceptors.request.use((config) => {
  const accessToken = getCookie("accessToken");

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
